/*
 *  This file hrone-snackbar.service.ts is part of HROne Inbox. The intelectual property owned by Uneecops Workplace Solution PVT. LTD.
 *  CopyrightYear: 2025
 *  (C) 2015-2025. All Right reserved. Uneecops Workplace Solution PVT. LTD.
 */

import {
  ApplicationRef,
  ComponentRef,
  EnvironmentInjector,
  Injectable,
  NgZone,
  createComponent,
  inject,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { HroneSnackbarComponent } from './hrone-snackbar.component';
import { HroneSnackbarRef } from './hrone-snackbar-ref';
import {
  IHroneSnackbarAction,
  IHroneSnackbarConfig,
  THroneSnackbarDismissReason,
  THroneSnackbarPosition,
  THroneSnackbarVariant,
} from './hrone-snackbar.model';

const DEFAULT_DURATION_MS = 5000;
const DEFAULT_POSITION: THroneSnackbarPosition = 'bottom-right';
/** Max cards on screen at once (whole screen, all positions combined); further calls wait in a FIFO queue. */
const MAX_VISIBLE = 3;

/** Legacy SnackbarAlertService validationType codes → variant. Unlisted codes fall back to 'success' (legacy green). */
const LEGACY_VALIDATION_TYPE_VARIANTS: Record<string, THroneSnackbarVariant> = {
  '0': 'success',
  '1': 'info',
  '2': 'danger',
  '3': 'warning',
  '5': 'pending',
};
// Must match the leave timing in hrone-snackbar.component.scss: card scale-out (50ms) runs while the
// slot collapses (200ms after a 50ms delay) so the cards still showing slide into the gap — 250ms total.
const LEAVE_ANIMATION_MS = 250;

interface ISnackbarEntry {
  ref: HroneSnackbarRef;
  config: IHroneSnackbarConfig;
  /** null while the entry waits in the queue — the component is only created when it is shown. */
  componentRef: ComponentRef<HroneSnackbarComponent> | null;
  /** The template's root .hrone-snackbar element, moved into its position's stack container. */
  panelEl: HTMLElement | null;
  timerId: ReturnType<typeof setTimeout> | null;
  isLeaving: boolean;
  /** Opened via the legacy Alert(message, validationType) shape — at most one such card exists (Material parity). */
  isLegacy: boolean;
}

/**
 * Opens hrone-snackbar cards — up to MAX_VISIBLE (3) stacked at once; further calls wait in a FIFO
 * queue and are shown, in order, as visible cards are removed. A queued card's auto-dismiss timer
 * starts only when it is shown.
 *
 * Legacy shape = one at a time (exact MatSnackBar parity): a legacy Alert(message, validationType)
 * immediately replaces the previous legacy-shape card (visible or queued; reason 'replaced'), so a later
 * message still clears an earlier sticky one and alerts fired in a loop leave only the last visible —
 * migrating a file from SnackbarAlertService changes no behavior. Config-shape cards stack; both kinds
 * share the same stacks and the MAX_VISIBLE limit.
 *
 * Stacking: each screen position gets one fixed .hrone-snackbar-stack container in <body> (created
 * on demand, removed when empty). Cards are appended in arrival order; the stylesheet puts the newest
 * nearest the screen edge (bottom for bottom-*, top for top-right).
 *
 * No Material / PrimeNG: the component is created with createComponent(), attached to
 * ApplicationRef (so change detection reaches it). Only the template's root .hrone-snackbar element
 * is moved into the stack container; the <hrone-snackbar> host element itself is never attached to the DOM.
 *
 * Zone safety: every public entry point runs inside NgZone.run() and the view is rendered
 * synchronously with detectChanges(), so calls from outside Angular's zone (runOutsideAngular,
 * third-party SDK callbacks, postMessage, …) still render immediately.
 */
@Injectable({ providedIn: 'root' })
export class HroneSnackbarService {
  private readonly _appRef = inject(ApplicationRef);
  private readonly _environmentInjector = inject(EnvironmentInjector);
  private readonly _zone = inject(NgZone);
  private readonly _document = inject(DOCUMENT);

  /** Cards on screen, oldest first — includes cards playing their exit animation (they keep their slot until removed). */
  private readonly _visible: ISnackbarEntry[] = [];
  /** Cards waiting for a free slot, oldest first. */
  private readonly _queue: ISnackbarEntry[] = [];
  private readonly _stacks = new Map<THroneSnackbarPosition, HTMLElement>();

  /**
   * The single public API — two call shapes:
   *
   * 1. New design system — one config object, returns a HroneSnackbarRef (dismiss / update / afterDismissed):
   *      this._snackbar.Alert({ variant: 'success', title: 'Saved', actions: [...] })
   *
   * 2. Legacy SnackbarAlertService.Alert() — same signature, returns void (so `return this._snackbar.Alert(...)`
   *    inside `: void` methods keeps compiling); migrating a consumer only swaps the import + injected type:
   *      this._snackbar.Alert(res.message, res.validationType)
   *    validationType: '0' success | '1' info | '2' danger | '3' warning | '5' pending; anything else → success.
   *    Numbers (e.g. 3) are accepted. '0' auto-closes after `duration` with no ×; every other type stays until ×.
   *    `action` is accepted for compatibility — the legacy 'OK' button is rendered as ×.
   *    One at a time, like MatSnackBar: each legacy call replaces the previous legacy-shape card.
   *
   * Shape is chosen by ARGUMENT COUNT, not by the first argument's type: legacy calls always pass at least
   * (message, validationType), and many pass an `any`-typed message (API fields such as res.message) — if one
   * is ever a non-string object at runtime it must still take the legacy path, never be read as a config.
   */
  Alert(config: IHroneSnackbarConfig): HroneSnackbarRef;
  Alert(message: string, validationType: string | number, action?: string, duration?: number): void;
  Alert(...args: [IHroneSnackbarConfig] | [string, string | number, string?, number?]): HroneSnackbarRef | void {
    if (args.length === 1 && typeof args[0] === 'object' && args[0] !== null) {
      return this._open(args[0]);
    }
    const [message, validationType, , duration = 1000] = args as [string, string | number, string?, number?];
    const type = String(validationType);
    const isSuccess = type === '0';
    this._open(
      {
        variant: LEGACY_VALIDATION_TYPE_VARIANTS[type] ?? 'success',
        message,
        duration: isSuccess ? duration : 0,
        closable: !isSuccess,
      },
      true,
    );
  }

  /**
   * Shows the card if a slot is free, otherwise queues it. With preventDuplicates, returns the existing card's ref instead.
   * isLegacy: first replaces the previous legacy-shape card, so the new one takes its slot.
   */
  private _open(config: IHroneSnackbarConfig, isLegacy = false): HroneSnackbarRef {
    return this._zone.run(() => {
      if (isLegacy) {
        this._replaceLegacy();
      } else if (config.preventDuplicates) {
        const duplicate = this._findDuplicate(config);
        if (duplicate) return duplicate.ref;
      }

      const entry: ISnackbarEntry = {
        config,
        componentRef: null,
        panelEl: null,
        timerId: null,
        isLeaving: false,
        isLegacy,
        ref: new HroneSnackbarRef(
          () => this._zone.run(() => this._dismiss(entry, 'manual')),
          (patch) => this._zone.run(() => this._update(entry, patch)),
        ),
      };

      if (this._visible.length < MAX_VISIBLE) {
        this._show(entry);
      } else {
        this._queue.push(entry);
      }
      return entry.ref;
    });
  }

  /** Dismiss every visible snackbar and drop the queued ones (they are never shown). */
  dismiss(): void {
    this._zone.run(() => {
      // Empty the queue first so freed slots are not refilled from it.
      this._queue.splice(0).forEach((entry) => entry.ref._notifyDismissed('manual'));
      [...this._visible].forEach((entry) => this._dismiss(entry, 'manual'));
    });
  }

  /** Creates the card, moves it into its position's stack and starts its timer. */
  private _show(entry: ISnackbarEntry): void {
    const componentRef = createComponent(HroneSnackbarComponent, {
      environmentInjector: this._environmentInjector,
    });
    componentRef.setInput('config', entry.config);
    this._appRef.attachView(componentRef.hostView);
    // Render while the host is still detached, then move only the inner panel into the stack.
    componentRef.changeDetectorRef.detectChanges();
    const hostEl: HTMLElement = componentRef.location.nativeElement;
    entry.componentRef = componentRef;
    entry.panelEl = hostEl.querySelector<HTMLElement>('.hrone-snackbar') as HTMLElement;

    // Output subscriptions end automatically when the component is destroyed.
    componentRef.instance.closeClick.subscribe(() => this._dismiss(entry, 'close'));
    componentRef.instance.actionClick.subscribe((action) => this._onAction(entry, action));

    this._visible.push(entry);
    const position = this._positionOf(entry.config);
    this._stackFor(position).appendChild(entry.panelEl);
    this._syncStack(position);
    this._startTimer(entry);
  }

  /**
   * Legacy-shape parity with MatSnackBar (one at a time): drops a queued legacy card and removes a visible one
   * immediately (no exit animation), both with reason 'replaced'. The freed slot is NOT refilled from the queue,
   * so the incoming legacy card takes it.
   */
  private _replaceLegacy(): void {
    for (const entry of this._queue.filter((queued) => queued.isLegacy)) {
      this._queue.splice(this._queue.indexOf(entry), 1);
      entry.ref._notifyDismissed('replaced');
    }
    for (const entry of this._visible.filter((visible) => visible.isLegacy)) {
      this._remove(entry, 'replaced', false);
    }
  }

  /** Fills free slots from the queue, oldest first. */
  private _showQueued(): void {
    while (this._visible.length < MAX_VISIBLE && this._queue.length) {
      this._show(this._queue.shift() as ISnackbarEntry);
    }
  }

  private _onAction(entry: ISnackbarEntry, action: IHroneSnackbarAction): void {
    action.handler?.();
    if (action.dismiss !== false) this._dismiss(entry, 'action');
  }

  /** Merges config into a visible card (re-rendered, timer restarted) or a queued one (used when it is shown). */
  private _update(entry: ISnackbarEntry, patch: IHroneSnackbarConfig): void {
    if (entry.isLeaving) return;
    if (this._queue.includes(entry)) {
      entry.config = { ...entry.config, ...patch };
      return;
    }
    if (!entry.componentRef || !entry.panelEl || !this._visible.includes(entry)) return;

    const previousPosition = this._positionOf(entry.config);
    entry.config = { ...entry.config, ...patch };
    entry.componentRef.setInput('config', entry.config);
    entry.componentRef.changeDetectorRef.detectChanges();

    const position = this._positionOf(entry.config);
    if (position !== previousPosition) {
      this._stackFor(position).appendChild(entry.panelEl);
      this._syncStack(previousPosition);
    }
    this._syncStack(position);
    this._startTimer(entry);
  }

  /** (Re)starts auto-dismiss from config.duration; 'pending' and duration 0 stay until dismissed. */
  private _startTimer(entry: ISnackbarEntry): void {
    this._clearTimer(entry);
    const { duration, variant } = entry.config;
    const ms = duration ?? (variant === 'pending' ? 0 : DEFAULT_DURATION_MS);
    if (ms > 0) {
      entry.timerId = setTimeout(() => this._dismiss(entry, 'timeout'), ms);
    }
  }

  private _clearTimer(entry: ISnackbarEntry): void {
    if (entry.timerId !== null) {
      clearTimeout(entry.timerId);
      entry.timerId = null;
    }
  }

  /** Queued: drops it without showing. Visible: plays the exit animation, then removes it. */
  private _dismiss(entry: ISnackbarEntry, reason: THroneSnackbarDismissReason): void {
    const queuedIndex = this._queue.indexOf(entry);
    if (queuedIndex !== -1) {
      this._queue.splice(queuedIndex, 1);
      entry.ref._notifyDismissed(reason);
      return;
    }
    if (!entry.componentRef || entry.isLeaving || !this._visible.includes(entry)) return;
    entry.isLeaving = true;
    this._clearTimer(entry);
    entry.componentRef.setInput('leaving', true);
    entry.componentRef.changeDetectorRef.detectChanges();
    setTimeout(() => this._remove(entry, reason), LEAVE_ANIMATION_MS);
  }

  /**
   * Detaches, destroys and removes the card immediately, then (unless fillFromQueue is false — legacy
   * replace, where the incoming card takes the slot) fills the freed slot from the queue.
   */
  private _remove(entry: ISnackbarEntry, reason: THroneSnackbarDismissReason, fillFromQueue = true): void {
    if (!entry.componentRef || entry.componentRef.hostView.destroyed) return;
    this._clearTimer(entry);
    this._appRef.detachView(entry.componentRef.hostView);
    entry.componentRef.destroy();
    // The panel was moved out of the (detached) host, so Angular won't remove it from the DOM.
    entry.panelEl?.remove();

    const index = this._visible.indexOf(entry);
    if (index !== -1) this._visible.splice(index, 1);
    this._syncStack(this._positionOf(entry.config));

    // Show the next queued card before notifying, so an Alert() opened from an afterDismissed()
    // subscriber can't jump ahead of cards that were already waiting.
    if (fillFromQueue) this._showQueued();
    entry.ref._notifyDismissed(reason);
  }

  private _positionOf(config: IHroneSnackbarConfig): THroneSnackbarPosition {
    return config.position ?? DEFAULT_POSITION;
  }

  /** The fixed stack container for a position — created and appended to <body> on first use. */
  private _stackFor(position: THroneSnackbarPosition): HTMLElement {
    let stack = this._stacks.get(position);
    if (!stack) {
      stack = this._document.createElement('div');
      stack.className = `hrone-snackbar-stack hrone-snackbar-stack-${position}`;
      this._document.body.appendChild(stack);
      this._stacks.set(position, stack);
    }
    return stack;
  }

  /**
   * Removes an empty stack container; otherwise applies the highest config.baseZIndex among its cards
   * (no baseZIndex → the stylesheet default).
   */
  private _syncStack(position: THroneSnackbarPosition): void {
    const stack = this._stacks.get(position);
    if (!stack) return;
    const entries = this._visible.filter((entry) => this._positionOf(entry.config) === position);
    if (!entries.length) {
      stack.remove();
      this._stacks.delete(position);
      return;
    }
    const zIndexes = entries
      .map((entry) => entry.config.baseZIndex)
      .filter((zIndex): zIndex is number => typeof zIndex === 'number');
    stack.style.zIndex = zIndexes.length ? String(Math.max(...zIndexes)) : '';
  }

  /** A visible (not leaving) or queued card with the same variant, title and message. */
  private _findDuplicate(config: IHroneSnackbarConfig): ISnackbarEntry | undefined {
    return [...this._visible.filter((entry) => !entry.isLeaving), ...this._queue].find(
      (entry) =>
        entry.config.variant === config.variant &&
        entry.config.title === config.title &&
        entry.config.message === config.message,
    );
  }
}
